import { Dispatch, Middleware, UnknownAction } from '@reduxjs/toolkit';
import SocketFactory, { SocketConnection, SocketInterface } from './utils/socketFactory';

import { settings } from '../constants/appSettings';
import { setConnectionStatus, setDataReloadResource, storeSocketId } from '../store/actions/sockets';
import { AppDispatch } from '../config/store-config';
import { State } from '../store/state/state';
import { TResourceTypeChange } from '../types';

enum SocketEvent {
  Connect = 'connect',
  Disconnect = 'disconnect',
  Error = 'err',
  dataReload = 'data_reload',
  healthCheck = 'health',
}

enum SocketActions {
  initSocket = 'sockets/initSocket',
  restartSockets = 'sockets/restartSockets',
  forceReconnect = 'sockets/forceReconnect',
  healthCheck = 'sockets/healthCheck',
}

let mainSocketConfig = {
  url: `${settings.api_host}/users`,
  bearer: true,
};

const initializeSocket = async (
  url: string,
  dispatch: AppDispatch,
  bearer: boolean = true,
  events?: (socket: SocketConnection, dispatch: AppDispatch) => void,
): Promise<SocketInterface> => {
  try {
    console.log(`Initializing socket connection to ${url} with bearer=${bearer}`);

    if (url === mainSocketConfig.url) {
      mainSocketConfig.bearer = bearer;
    }

    const connection = await SocketFactory.create(url, bearer);

    // Make sure we set up socket events
    events && events(connection, dispatch);

    console.log(`Socket initialized successfully for ${url}`);
    return connection;
  } catch (error) {
    console.error(`Failed to initialize socket for ${url}:`, error);
    throw error;
  }
};

// Safely disconnect a socket
const safelyDisconnectSocket = (socket: SocketInterface | undefined): Promise<void> => {
  return new Promise((resolve) => {
    if (!socket || !socket.socket) {
      console.log('No socket to disconnect');
      resolve();
      return;
    }

    // If already disconnected, resolve immediately
    if (!socket.socket.connected) {
      console.log('Socket already disconnected');
      resolve();
      return;
    }

    try {
      // Set up one-time disconnect listener to know when it's done
      socket.socket.once('disconnect', () => {
        console.log('Socket disconnected successfully');
        resolve();
      });

      // Set a timeout in case the event doesn't fire
      const timeout = setTimeout(() => {
        console.log('Socket disconnect timed out, proceeding anyway');
        resolve();
      }, 1000);

      // Initiate disconnect
      socket.socket.disconnect();

      // Also resolve if it's still connected after timeout
      setTimeout(() => {
        if (socket.socket.connected) {
          console.log('Socket still connected after disconnect call, forcing cleanup');
          clearTimeout(timeout);
          resolve();
        }
      }, 1200);
    } catch (error) {
      console.error('Error during socket disconnect:', error);
      resolve(); // Continue despite error
    }
  });
};

// Also remove the socket from SocketFactory map to ensure clean recreation
const cleanupSocket = async (socket: SocketInterface | undefined, url: string): Promise<void> => {
  if (!socket) return;

  await safelyDisconnectSocket(socket);
  try {
    // Remove from factory to ensure clean state for reconnection
    SocketFactory.remove(url);
  } catch (error) {
    console.error(`Error removing socket from factory for ${url}:`, error);
  }
};

const socketEvents = (socket: SocketConnection, dispatch: AppDispatch) => {
  socket.socket.on(SocketEvent.Connect, () => {
    dispatch(setConnectionStatus(true));
    console.log('Main socket connected with ID:', socket.socket.id);
    dispatch(storeSocketId(socket.socket.id));
  });

  socket.socket.on(SocketEvent.Disconnect, () => {
    dispatch(setConnectionStatus(false));
    console.log('Main socket disconnected');
  });

  socket.socket.on(SocketEvent.Error, (message) => {
    console.error('Main socket error:', message);
  });

  socket.socket.on(SocketEvent.dataReload, (data: TResourceTypeChange[]) => {
    console.log('Received data reload event:', data);
    dispatch(setDataReloadResource(data));
  });

  socket.socket.io.on('reconnect', (attempt) => {
    console.log(`Main socket reconnected after ${attempt} attempts`);
    dispatch(setConnectionStatus(true));
  });

  socket.socket.io.on('reconnect_attempt', (attempt) => {
    console.log(`Main socket reconnection attempt #${attempt}`);
  });

  socket.socket.io.on('reconnect_error', (error) => {
    console.error('Main socket reconnection error:', error);
  });

  socket.socket.io.on('reconnect_failed', () => {
    console.error('Main socket reconnection failed');
  });
};

const handleSocketActions = (socket: SocketInterface | undefined, action: any) => {
  if (!socket) return;
  switch (action.type) {
    case SocketActions.healthCheck:
      console.log('Emitting healthCheck event', action.payload);
      socket.socket.emit(SocketEvent.healthCheck, action.payload);
      break;
    case SocketActions.forceReconnect:
      if (socket.socket && !socket.socket.connected) {
        console.log('Forcing socket reconnection');
        socket.socket.connect();
      }
      break;

    default:
      break;
  }
};

const socketMiddleware: Middleware<{}, State> = ({ dispatch, getState }) => {
  let connection: SocketInterface | undefined;
  let restartInProgress = false;

  return (next) => async (action: any) => {
    try {
      switch (action.type) {
        case SocketActions.initSocket:
          console.log('Processing initSocket action');
          // Clean up existing socket if it exists
          if (connection && typeof window !== 'undefined') {
            console.log('Closing existing global socket connection');
            await cleanupSocket(connection, mainSocketConfig.url);
            connection = undefined; // Clear the reference
          }

          // Initialize new socket
          if (typeof window !== 'undefined') {
            console.log('Initializing new global socket connection');
            try {
              connection = await initializeSocket(
                mainSocketConfig.url,
                dispatch,
                mainSocketConfig.bearer,
                socketEvents,
              );
              console.log('Global socket connection established');
            } catch (error) {
              console.error('Failed to initialize global socket:', error);
            }
          }
          break;

        case SocketActions.restartSockets:
          if (restartInProgress) {
            console.log('Restart already in progress, skipping');
            break;
          }

          console.log('Starting socket restart sequence');
          restartInProgress = true;

          try {
            // First, disconnect existing sockets
            console.log('Disconnecting global socket');
            if (connection && typeof window !== 'undefined') {
              await cleanupSocket(connection, mainSocketConfig.url);
              connection = undefined;
            }

            // Allow time for disconnections to complete
            await new Promise((resolve) => setTimeout(resolve, 500));

            // Then recreate sockets sequentially
            if (typeof window !== 'undefined') {
              console.log('Reinitializing global socket with bearer:', mainSocketConfig.bearer);
              try {
                connection = await initializeSocket(
                  mainSocketConfig.url,
                  dispatch,
                  mainSocketConfig.bearer,
                  socketEvents,
                );
                console.log('Global socket reinitialized successfully');
              } catch (error) {
                console.error('Failed to reinitialize global socket:', error);
              }

              setTimeout(() => {
                console.log('Checking socket connections after restart');
                if (connection && !connection.socket.connected) {
                  console.log('Global socket not connected after restart, forcing reconnect');
                  dispatch({ type: SocketActions.forceReconnect });
                }
                restartInProgress = false;
              }, 1000);
            } else {
              restartInProgress = false;
            }
          } catch (error) {
            console.error('Error during socket restart:', error);
            restartInProgress = false;
          }
          break;

        default:
          handleSocketActions(connection, action);
          break;
      }
    } catch (error) {
      console.error('Socket middleware error:', error);
    }

    return next(action);
  };
};

export default socketMiddleware;
